/**
 * Pickup Slots — operational board for core-service platform slots S1..S5.
 * Per-slot demand (from the backend SlotPickup mirror), close countdowns,
 * and officer actions (close slot, relay merchant response, mark missed,
 * override daily limit, RTO-coupling append) — actions wired in via modals.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarClock, Clock, RefreshCw, Truck } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { cn } from '../utils/cn';
import { getAssignmentPanel, getCouplingSuggestions, getSlotBoard, markMissed, rollToNextSlot } from '../services/pickupSlots.api';
import type { CouplingSuggestion, PickupSlotCode, ShiftBoardEntry, SlotBoardRow, SlotBoardSlot } from '../services/pickupSlots.api';
import ShiftTriggerModal from '../components/pickupSlots/ShiftTriggerModal';
import {
  SLOT_STATE_LABELS,
  getBaghdadOperationalDate,
  getSlotCloseBadgeVariant,
  getSlotCloseStatus,
  getSlotStateBadgeClasses,
} from '../utils/pickupSlotStatus';
import { formatTimeBaghdad } from '../utils/date';
import CloseSlotModal from '../components/pickupSlots/CloseSlotModal';
import SlotResponseModal from '../components/pickupSlots/SlotResponseModal';
import OverrideDailyLimitModal from '../components/pickupSlots/OverrideDailyLimitModal';
import CoupleSuggestionModal from '../components/pickupSlots/CoupleSuggestionModal';
import { useCan } from '../contexts/useAuth';

export type PickupSlotsModal =
  | { kind: 'close-slot'; slotCode: PickupSlotCode }
  | { kind: 'slot-response'; row: SlotBoardRow }
  | { kind: 'mark-missed'; row: SlotBoardRow }
  | { kind: 'roll-over'; row: SlotBoardRow; nextSlot: SlotBoardSlot }
  | { kind: 'override-daily-limit' }
  | { kind: 'couple'; row: SlotBoardRow; suggestion: CouplingSuggestion }
  | { kind: 'shift-trigger'; shift: ShiftBoardEntry }
  | null;

/** States an officer may roll to a later slot (same gate as the close sweep's candidates). */
const ROLLABLE_STATES = ['slot_assigned', 'route_included', 'route_appended'];

/** Shift-strip status: generation marker wins; otherwise countdown to the trigger. */
function getShiftGenStatus(
  shift: ShiftBoardEntry,
  now: Date
): { label: string; variant: 'success' | 'warning' | 'danger' | 'default' | 'info' } {
  const gen = shift.generation;
  if (gen?.status === 'generating') return { label: 'Generating routes…', variant: 'info' };
  if (gen?.status === 'failed') return { label: 'Generation failed', variant: 'danger' };
  if (gen?.status === 'no_demand') return { label: 'No demand', variant: 'default' };
  if (gen?.status === 'generated') {
    // `partial` zones DID get routes — they just left work behind, so they count
    // as ready here and the shortfall is called out separately below. Reporting
    // them as not-ready would read as "no route in Zone C", which is wrong.
    const ready = gen.zones.filter(
      (z) => z.status === 'generated' || z.status === 'partial'
    ).length;
    const unassigned = gen.zones.reduce((sum, z) => sum + (z.unassigned_count ?? 0), 0);
    if (unassigned > 0) {
      return {
        label: `Routes ready · ${ready}/${gen.zones.length} zones · ${unassigned} unplaced`,
        variant: 'warning',
      };
    }
    return { label: `Routes ready · ${ready}/${gen.zones.length} zones`, variant: 'success' };
  }
  const untilTriggerMin = Math.floor((new Date(shift.trigger_instant).getTime() - now.getTime()) / 60_000);
  if (untilTriggerMin > 0) {
    const label =
      untilTriggerMin >= 60
        ? `Generates in ${Math.floor(untilTriggerMin / 60)}h ${untilTriggerMin % 60}m`
        : `Generates in ${untilTriggerMin}m`;
    return { label, variant: 'info' };
  }
  if (now.getTime() < new Date(shift.cutoff_instant).getTime()) {
    return { label: 'Trigger due', variant: 'warning' };
  }
  return { label: 'Window closed', variant: 'default' };
}

export default function PickupSlots() {
  const can = useCan();
  const [opDate, setOpDate] = useState<string>(() => getBaghdadOperationalDate());
  const [activeTab, setActiveTab] = useState<PickupSlotCode>('S1');
  const [activeModal, setActiveModal] = useState<PickupSlotsModal>(null);
  const [now, setNow] = useState(() => new Date());

  // local 30s tick keeps countdowns fresh without extra requests
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Force a Core re-sync on the next fetch (page mount, manual Refresh click).
  // Auto-polling (refetchInterval below) must NOT set this — the backend only
  // debounces refresh=true calls ~20s, so polling every 30s must stay "cheap".
  const forceBoardRefreshRef = useRef(true);
  const boardQuery = useQuery({
    queryKey: ['pickupSlotBoard', opDate],
    queryFn: () => {
      const refresh = forceBoardRefreshRef.current;
      forceBoardRefreshRef.current = false;
      return getSlotBoard(opDate, refresh);
    },
    refetchInterval: 30_000,
  });

  // Same one-shot refresh on first load only; suggestions ride the board's
  // polling cadence otherwise and never force a re-sync themselves.
  const forceSuggestionsRefreshRef = useRef(true);
  const suggestionsQuery = useQuery({
    queryKey: ['pickupSlotCoupling', opDate],
    queryFn: () => {
      const refresh = forceSuggestionsRefreshRef.current;
      forceSuggestionsRefreshRef.current = false;
      return getCouplingSuggestions(opDate, refresh);
    },
    refetchInterval: 60_000,
  });

  const panelQuery = useQuery({
    queryKey: ['pickupSlotAssignmentPanel', opDate],
    queryFn: () => getAssignmentPanel(opDate),
    refetchInterval: 60_000,
  });

  const handleRefresh = () => {
    forceBoardRefreshRef.current = true;
    // Also force the suggestions' full re-sync: a just-assigned RTO's
    // pickup_agent is only visible to a FULL feed re-fetch (backend forceFull).
    forceSuggestionsRefreshRef.current = true;
    boardQuery.refetch();
    suggestionsQuery.refetch();
  };

  const suggestionByPickup = useMemo(
    () => new Map((suggestionsQuery.data ?? []).map((s) => [s.pickup_uid, s])),
    [suggestionsQuery.data]
  );

  const slots = boardQuery.data?.slots ?? [];
  const shifts = boardQuery.data?.shifts ?? [];
  // Freshest core-mirror sync across the slots — the board's staleness signal.
  const lastSyncedAt = slots.reduce<string | null>(
    (max, s) => (s.last_synced_at && (!max || s.last_synced_at > max) ? s.last_synced_at : max),
    null
  );
  const activeSlot = slots.find((s) => s.code === activeTab);
  // Roll-over target for rows in this tab: the next ACTIVE slot after it (undefined on the last one)
  const activeSlotIdx = slots.findIndex((s) => s.code === activeTab);
  const nextOpenSlot =
    activeSlotIdx >= 0 ? slots.slice(activeSlotIdx + 1).find((s) => s.active !== false) : undefined;
  const isDisabled = (boardQuery.error as { response?: { status?: number } } | null)?.response?.status === 503;

  if (isDisabled) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center">
          <CalendarClock className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm font-medium text-gray-700">
            Pickup Slots is not enabled on this environment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CalendarClock className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Pickup Slots</h1>
            <p className="text-sm text-gray-500">
              Operational day (04:00–04:00 Baghdad) demand per platform slot
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastSyncedAt && (
            <span className="text-xs text-gray-500" title={`Core mirror last synced ${lastSyncedAt}`}>
              Synced {formatTimeBaghdad(lastSyncedAt)}
            </span>
          )}
          <input
            type="date"
            value={opDate}
            onChange={(e) => setOpDate(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
          <button
            onClick={handleRefresh}
            disabled={boardQuery.isFetching}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4', boardQuery.isFetching && 'animate-spin')} />
            Refresh
          </button>
          {can('slots.override') && (
            <button
              onClick={() => setActiveModal({ kind: 'override-daily-limit' })}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Override daily limit
            </button>
          )}
        </div>
      </div>

      {/* Closed-day banner (holiday calendar) */}
      {boardQuery.data?.closed_day?.closed && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-semibold">{opDate} is a pickup closed day</span>
          {boardQuery.data.closed_day.reason ? ` — ${boardQuery.data.closed_day.reason}` : ''}
          {'. '}
          Core rolls pickups landing on this date to the next open day
          {boardQuery.data.closed_day.next_open_date ? ` (${boardQuery.data.closed_day.next_open_date})` : ''}.
        </div>
      )}

      {/* Shift strip — internal driver shifts (route-generation scope in shift mode).
          Generation markers are keyed SHIFT1/SHIFT2, so their status lives here,
          not on the merchant slot chips below. */}
      {shifts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {shifts.map((shift) => {
            const status = getShiftGenStatus(shift, now);
            const coversActive = shift.slot_codes.includes(activeTab);
            const genZones = shift.generation?.zones ?? [];
            const noDriverZones = genZones.filter((z) => z.status === 'no_drivers');
            const ungroupedZones = shift.generation?.ungrouped_zone_names ?? [];
            const unplacedZones = genZones.filter((z) => (z.unassigned_count ?? 0) > 0);
            const groupCodes = [
              ...new Set(genZones.map((z) => z.group_code).filter((c): c is string => !!c)),
            ].sort();
            const triggerBaghdad = new Date(shift.trigger_instant).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'Asia/Baghdad',
            });
            return (
              <div
                key={shift.code}
                role="button"
                tabIndex={0}
                onClick={() => {
                  const first = shift.slot_codes[0] as PickupSlotCode | undefined;
                  if (first) setActiveTab(first);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const first = shift.slot_codes[0] as PickupSlotCode | undefined;
                    if (first) setActiveTab(first);
                  }
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                  coversActive
                    ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                )}
                title={`${shift.code} covers ${shift.slot_codes.join(', ')} — route generation triggers at ${triggerBaghdad} Baghdad`}
              >
                <Truck className="h-4 w-4 text-indigo-600" />
                <span className="font-semibold">{shift.code}</span>
                <span className="text-xs text-gray-500">{shift.slot_codes.join('+')}</span>
                <span className="text-xs text-gray-500">
                  {shift.window.from}–{shift.window.to}
                </span>
                <Badge size="sm" variant={status.variant}>{status.label}</Badge>
                {shift.trigger_override && (
                  <Badge size="sm" variant="default">custom trigger</Badge>
                )}
                {(shift.generation?.held_count ?? 0) > 0 && (
                  <Badge size="sm" variant="warning">{shift.generation?.held_count} held — no zone</Badge>
                )}
                {noDriverZones.length > 0 && (
                  <Badge size="sm" variant="warning">
                    {noDriverZones.map((z) => z.zone_name).join(', ')}: no drivers
                  </Badge>
                )}
                {/* These zones WERE routed — on their own, because they carry no
                    group. Nothing is stuck, so this is a warning and not a
                    failure; it is just the only trace that grouping did not
                    apply, and ops has to go set the group. */}
                {ungroupedZones.length > 0 && (
                  <Badge
                    size="sm"
                    variant="warning"
                    title="Routed on their own because no zone group is set — set the group to include them"
                  >
                    {ungroupedZones.join(', ')}: no zone group
                  </Badge>
                )}
                {/* The solver placed routes but not everything. This is the only
                    place "exceed the cap → add a route, never drop" shows up as
                    broken, so it must not be buried. */}
                {unplacedZones.length > 0 && (
                  <Badge
                    size="sm"
                    variant="warning"
                    title={unplacedZones
                      .map((z) => `${z.zone_name}: ${z.unassigned_count} unplaced`)
                      .join(' · ')}
                  >
                    {unplacedZones.reduce((sum, z) => sum + (z.unassigned_count ?? 0), 0)} task(s)
                    on no route
                  </Badge>
                )}
                {groupCodes.length > 0 && (
                  <Badge
                    size="sm"
                    variant="info"
                    title={`Solved as zone group(s) ${groupCodes.join(', ')} — one route set per group instead of per zone`}
                  >
                    grouped: {groupCodes.join(', ')}
                  </Badge>
                )}
                {can('slots.settings') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveModal({ kind: 'shift-trigger', shift });
                    }}
                    className="rounded p-1 text-gray-400 hover:bg-indigo-100 hover:text-indigo-700"
                    title="Change route-generation time"
                  >
                    <Clock className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Slot tabs with countdowns */}
      <div className="flex flex-wrap gap-2">
        {slots.map((slot) => {
          const status = getSlotCloseStatus(slot.close_instant, slot.closed_at, now);
          return (
            <button
              key={slot.code}
              onClick={() => setActiveTab(slot.code)}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                activeTab === slot.code
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              )}
            >
              <span className="font-semibold">{slot.code}</span>
              <span className="text-xs text-gray-500">
                {slot.window.from}–{slot.window.to}
              </span>
              {slot.active === false && (
                <Badge size="sm" variant="default">Inactive</Badge>
              )}
              <Badge size="sm" variant="info">{slot.counts.total}</Badge>
              <Badge size="sm" variant={getSlotCloseBadgeVariant(status.state)}>{status.label}</Badge>
            </button>
          );
        })}
      </div>

      {/* Active slot panel */}
      {boardQuery.isLoading ? (
        <div className="py-16 text-center text-gray-500">Loading slot demand…</div>
      ) : boardQuery.isError ? (
        <div className="py-16 text-center text-red-600">Failed to load the slot board.</div>
      ) : activeSlot ? (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">{activeSlot.title}</span>{' '}
              · {activeSlot.counts.total} pickups
              {(activeSlot.cancelled_count ?? 0) > 0 && (
                <span className="ml-1 text-xs text-gray-500">
                  · {activeSlot.cancelled_count} cancelled in core
                </span>
              )}
              {activeSlot.generation && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs">
                  {activeSlot.generation.status === 'generating' && (
                    <Badge size="sm" variant="default">Generating routes…</Badge>
                  )}
                  {activeSlot.generation.status === 'no_demand' && (
                    <Badge size="sm" variant="default">Auto-routes: no demand</Badge>
                  )}
                  {activeSlot.generation.status === 'failed' && (
                    <Badge size="sm" variant="danger">Route generation failed</Badge>
                  )}
                  {activeSlot.generation.status === 'generated' && (
                    <>
                      <Badge size="sm" variant="success">
                        Auto-routes ready · {activeSlot.generation.zones.filter((z) => z.status === 'generated').length}/{activeSlot.generation.zones.length} zones
                      </Badge>
                      {activeSlot.generation.zones.some((z) => z.status === 'no_drivers') && (
                        <Badge size="sm" variant="warning">
                          {activeSlot.generation.zones.filter((z) => z.status === 'no_drivers').map((z) => z.zone_name).join(', ')}: no drivers
                        </Badge>
                      )}
                      {activeSlot.generation.held_count > 0 && (
                        <Badge size="sm" variant="warning">{activeSlot.generation.held_count} held — no zone</Badge>
                      )}
                    </>
                  )}
                </span>
              )}
              {activeSlot.closed_at && (
                <Badge size="sm" variant="default" className="ml-2">Closed</Badge>
              )}
            </div>
            {can('slots.close') && (
              <button
                onClick={() => setActiveModal({ kind: 'close-slot', slotCode: activeSlot.code })}
                disabled={!!activeSlot.closed_at}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium',
                  activeSlot.closed_at
                    ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                    : 'bg-amber-600 text-white hover:bg-amber-700'
                )}
              >
                Close slot now
              </button>
            )}
          </div>

          {(() => {
            // Shift mode returns SHIFT1/SHIFT2 entries spanning several slots —
            // resolve the active merchant slot to its covering entry.
            const panelSlot = panelQuery.data?.find(
              (p) => p.code === activeSlot.code || p.slot_codes?.includes(activeSlot.code)
            );
            if (!panelSlot || (panelSlot.zones.length === 0 && panelSlot.held_pickup_uids.length === 0)) return null;
            const isShiftEntry = panelSlot.code !== activeSlot.code;
            return (
              <div className="border-b border-gray-100 bg-gray-50/60 px-4 py-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-gray-500">
                  Assignment panel
                  {isShiftEntry && (
                    <Badge size="sm" variant="info">
                      {panelSlot.title} · {panelSlot.window.from}–{panelSlot.window.to}
                    </Badge>
                  )}
                  {panelSlot.final_leg && (
                    <Badge size="sm" variant="warning">Final leg — appends closed</Badge>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-500">
                        <th className="py-1 pr-4">Zone</th>
                        <th className="py-1 pr-4">Pickups</th>
                        <th className="py-1 pr-4">Orders</th>
                        <th className="py-1 pr-4">Required PA</th>
                        <th className="py-1 pr-4">Available</th>
                        <th className="py-1 pr-4">Shortfall</th>
                        <th className="py-1 pr-4">Route</th>
                      </tr>
                    </thead>
                    <tbody>
                      {panelSlot.zones.map((z) => (
                        <tr key={z.zone_id} className="border-t border-gray-100">
                          <td className="py-1.5 pr-4 font-medium text-gray-800">{z.zone_name}</td>
                          <td className="py-1.5 pr-4">{z.demand_count}</td>
                          <td className="py-1.5 pr-4">{z.order_count}</td>
                          <td className="py-1.5 pr-4">{z.required_pa}</td>
                          <td className="py-1.5 pr-4">{z.available_pa}</td>
                          <td className="py-1.5 pr-4">
                            {z.shortfall > 0 ? (
                              <Badge size="sm" variant="danger">−{z.shortfall}</Badge>
                            ) : (
                              <span className="text-gray-500">0</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-4">
                            {z.run_id ? (
                              <Link to="/routes" className="text-blue-600 hover:underline">
                                Open run →
                              </Link>
                            ) : (
                              <span className="text-gray-500">{z.run_status ?? 'not generated'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {panelSlot.held_pickup_uids.length > 0 && (
                  <div className="mt-2 text-xs text-amber-700">
                    {panelSlot.held_pickup_uids.length} pickup(s) matched no zone — assign manually from the list below.
                  </div>
                )}
              </div>
            );
          })()}

          {activeSlot.rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">No demand in this slot.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-2">Merchant</th>
                    <th className="px-4 py-2">Province</th>
                    <th className="px-4 py-2">Orders</th>
                    <th className="px-4 py-2">Preferred window</th>
                    <th className="px-4 py-2">State</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSlot.rows.map((row) => {
                    const suggestion = suggestionByPickup.get(row.pickup_uid);
                    return (
                      <tr key={row.pickup_uid} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-900">{row.merchant_name || '—'}</div>
                          <div className="text-xs text-gray-500">{row.platform_code}</div>
                        </td>
                        <td className="px-4 py-2 text-gray-600">{row.address?.province ?? '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{row.order_count}</td>
                        <td className="px-4 py-2">
                          {row.preferred_window ? (
                            <Badge
                              variant="default"
                              className="bg-purple-100 text-[10px] text-purple-700 border-purple-200 whitespace-nowrap"
                              title="Merchant's preferred timing (best effort)"
                            >
                              <Clock className="mr-1 h-3 w-3" />
                              {row.preferred_window.start}-{row.preferred_window.end}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-500">Anytime</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <Badge
                            variant="default"
                            className={cn('text-[10px] whitespace-nowrap', getSlotStateBadgeClasses(row.local_slot_state))}
                            title={
                              row.core_slot_state === row.local_slot_state
                                ? `In sync with core — ${SLOT_STATE_LABELS[row.core_slot_state] ?? row.core_slot_state} (${row.core_slot_state})`
                                : `Core still shows: ${SLOT_STATE_LABELS[row.core_slot_state] ?? row.core_slot_state} (${row.core_slot_state}) — fleet push in flight`
                            }
                          >
                            {SLOT_STATE_LABELS[row.local_slot_state] ?? row.local_slot_state}
                          </Badge>
                          {row.is_terminal && (
                            <Badge variant="danger" size="sm" className="ml-1">Terminal</Badge>
                          )}
                          {row.planned_route && (
                            <div className="mt-1">
                              <Link
                                to="/routes"
                                className="text-[10px] text-indigo-600 hover:underline whitespace-nowrap"
                                title={`Route ${row.planned_route.route_name} (${row.planned_route.route_status})${row.planned_route.driver_name ? ' · ' + row.planned_route.driver_name : ''}`}
                              >
                                On {row.planned_route.route_status} route ↗
                              </Link>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            {suggestion && !row.is_terminal && can('routes.edit') && (
                              <button
                                onClick={() => setActiveModal({ kind: 'couple', row, suggestion })}
                                className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-100"
                                title={`This merchant has an open RTO carried by ${suggestion.driver_name ?? suggestion.driver_uid}`}
                              >
                                <Truck className="h-3 w-3" />
                                Append to {suggestion.driver_name ?? 'PA'}
                              </button>
                            )}
                            {['rolled_over', 'next_day_fallback'].includes(row.local_slot_state) && can('slots.act') && (
                              <button
                                onClick={() => setActiveModal({ kind: 'slot-response', row })}
                                className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                              >
                                Record response
                              </button>
                            )}
                            {nextOpenSlot && !row.is_terminal && can('slots.act') && ROLLABLE_STATES.includes(row.local_slot_state) && (
                              <button
                                onClick={() => setActiveModal({ kind: 'roll-over', row, nextSlot: nextOpenSlot })}
                                className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100"
                                title={`Move this pickup to ${nextOpenSlot.code} (${nextOpenSlot.window.from}–${nextOpenSlot.window.to}) — the merchant will be asked to confirm`}
                              >
                                Roll to {nextOpenSlot.code}
                              </button>
                            )}
                            {!row.is_terminal && can('slots.mark_missed') && row.local_slot_state !== 'missed' && (
                              <button
                                onClick={() => setActiveModal({ kind: 'mark-missed', row })}
                                className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                              >
                                Mark missed
                              </button>
                            )}
                            {row.task_uid && (
                              <Link
                                to="/tasks"
                                className="text-xs text-blue-600 hover:underline"
                                title={row.task_uid}
                              >
                                Task ↗
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Modals mount here */}
      {activeModal?.kind === 'close-slot' && activeSlot && (
        <CloseSlotModal
          slotCode={activeModal.slotCode}
          operationalDate={opDate}
          pendingCount={
            (activeSlot.counts['slot_assigned'] ?? 0) +
            (activeSlot.counts['route_included'] ?? 0) +
            (activeSlot.counts['route_appended'] ?? 0)
          }
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.kind === 'slot-response' && (
        <SlotResponseModal row={activeModal.row} onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.kind === 'override-daily-limit' && (
        <OverrideDailyLimitModal onClose={() => setActiveModal(null)} />
      )}
      {activeModal?.kind === 'shift-trigger' && (
        <ShiftTriggerModal
          // Live lookup so a save immediately refreshes "current trigger" and
          // shows the Reset button — the open-time snapshot is only a fallback.
          shift={shifts.find((s) => s.code === activeModal.shift.code) ?? activeModal.shift}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.kind === 'couple' && (
        <CoupleSuggestionModal
          row={activeModal.row}
          suggestion={activeModal.suggestion}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.kind === 'mark-missed' && (
        <MarkMissedConfirm
          row={activeModal.row}
          operationalDate={opDate}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.kind === 'roll-over' && (
        <RollOverConfirm
          row={activeModal.row}
          nextSlot={activeModal.nextSlot}
          operationalDate={opDate}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  );
}

function RollOverConfirm({
  row,
  nextSlot,
  operationalDate,
  onClose,
}: {
  row: SlotBoardRow;
  nextSlot: SlotBoardSlot;
  operationalDate: string;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => rollToNextSlot(row.pickup_uid, operationalDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickupSlotBoard'] });
      onClose();
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      setError(err.response?.data?.error ?? 'Failed to roll the pickup over'),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">
          Roll to {nextSlot.code} ({nextSlot.window.from}–{nextSlot.window.to})?
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {row.merchant_name} ({row.platform_code}) moves to the <b>{nextSlot.title}</b> slot and the
          merchant is asked to confirm — accept, pick another slot, or defer to tomorrow (30-minute
          response window). Use this when the driver can't reach this merchant but the rest of the
          slot is fine.
        </p>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Rolling…' : `Roll to ${nextSlot.code}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkMissedConfirm({
  row,
  operationalDate,
  onClose,
}: {
  row: SlotBoardRow;
  operationalDate: string;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => markMissed(row.pickup_uid, operationalDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickupSlotBoard'] });
      onClose();
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      setError(err.response?.data?.error ?? 'Failed to mark missed'),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Mark pickup as missed?</h2>
        <p className="mt-2 text-sm text-gray-600">
          {row.merchant_name} ({row.platform_code}) will be marked <b>missed</b> — no rollover target,
          and the merchant is notified. Use only when the pickup cannot be rescued today.
        </p>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Marking…' : 'Mark missed'}
          </button>
        </div>
      </div>
    </div>
  );
}
